import { useState } from "react";
import type { ContentPiece } from "../../../../shared/types";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";

export const PLATFORM_LIMITS: Record<string, number> = {
  linkedin: 3000,
  twitter: 280,
  instagram: 2200,
  threads: 500,
  tiktok: 2200,
  youtube: 5000,
  bluesky: 300,
};

export const platformLabels: Record<string, string> = {
  linkedin: "LinkedIn",
  twitter: "Twitter/X",
  instagram: "Instagram",
  threads: "Threads",
  tiktok: "TikTok",
  youtube: "YouTube",
  bluesky: "Bluesky",
};

const platformIcons: Record<string, string> = {
  linkedin: "in",
  twitter: "X",
  instagram: "IG",
  threads: "@",
  tiktok: "TT",
  youtube: "YT",
  bluesky: "BS",
};

const statusConfig: Record<string, { variant: "default" | "success" | "error"; label: string }> = {
  draft: { variant: "default", label: "Draft" },
  approved: { variant: "success", label: "Approved" },
  rejected: { variant: "error", label: "Rejected" },
  published: { variant: "success", label: "Published" },
};

interface ContentCardProps {
  piece: ContentPiece;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onEdit: (id: string) => void;
  onRegenerate: (id: string) => void;
}

export function ContentCard({
  piece,
  onApprove,
  onReject,
  onEdit,
  onRegenerate,
}: ContentCardProps) {
  const [expanded, setExpanded] = useState(false);
  const limit = PLATFORM_LIMITS[piece.platform] || 5000;
  const isOverLimit = piece.character_count > limit;
  const isRejected = piece.status === "rejected";
  const isApproved = piece.status === "approved";

  // Truncate content to ~3 lines (~150 chars) when collapsed
  const truncateLength = 150;
  const needsTruncation = piece.content.length > truncateLength;
  const displayContent =
    expanded || !needsTruncation
      ? piece.content
      : piece.content.slice(0, truncateLength) + "\u2026";

  return (
    <div
      className={cn(
        "relative bg-white rounded-xl border border-slate-200 shadow-sm p-6 transition-all",
        isRejected && "opacity-60",
        isApproved && "ring-2 ring-green-200",
      )}
    >
      {/* Approved overlay checkmark */}
      {isApproved && (
        <div className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-600">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.5 12.75l6 6 9-13.5"
            />
          </svg>
        </div>
      )}

      {/* Platform header */}
      <div className="mb-3 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-xs font-bold text-slate-600">
          {platformIcons[piece.platform] || "?"}
        </span>
        <span className="text-sm font-semibold text-slate-900">
          {platformLabels[piece.platform] || piece.platform}
        </span>
        <div className="ml-auto">
          <Badge variant={statusConfig[piece.status]?.variant || "default"}>
            {statusConfig[piece.status]?.label || piece.status}
          </Badge>
        </div>
      </div>

      {/* Content preview */}
      <div className="mb-3">
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
          {displayContent}
        </p>
        {needsTruncation && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="mt-1 text-xs font-medium text-brand-600 hover:text-brand-700"
          >
            {expanded ? "Show less" : "Show more"}
          </button>
        )}
      </div>

      {/* Character count */}
      <div className="mb-3 text-xs text-slate-500">
        <span
          className={cn(
            "font-medium",
            isOverLimit ? "text-red-600" : "text-slate-700",
          )}
        >
          {piece.character_count}
        </span>
        /{limit}
        {isOverLimit && (
          <span className="ml-1 text-red-600">Over limit</span>
        )}
      </div>

      {/* Hashtag pills */}
      {piece.hashtags.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {piece.hashtags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={() => onApprove(piece.id)}
          disabled={isApproved}
          className="bg-green-600 text-white hover:bg-green-700"
        >
          Approve
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onEdit(piece.id)}
          className="bg-blue-50 text-blue-700 hover:bg-blue-100"
        >
          Edit
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={() => onReject(piece.id)}
          disabled={isRejected}
        >
          Reject
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onRegenerate(piece.id)}
          className="bg-amber-50 text-amber-700 hover:bg-amber-100"
        >
          Regenerate
        </Button>
      </div>
    </div>
  );
}
